<script setup lang="ts">
import { faAngleLeft } from '@fortawesome/pro-regular-svg-icons';
import { parse } from 'qs';
import type TripPackageSet from '@itinerary/trip/TripPackageSet';
import { type TripSearchForm } from '@search/trip/TripSearchForm';

const itineraryStore = useItineraryStore();
const searchFormStore = useSearchFormStore();
const currentSearchForm = useCurrentSearchForm<TripSearchForm>();
const router = useRouter();
const suggestion = computed(() => itineraryStore.currentAltSearchSuggestion);
const show = computed(() => itineraryStore.showSearchSuggestion);
const departureDate = computed(() => {
    const date = currentSearchForm.value.departureDate;
    if (!date) {
        return '';
    }
    return dayConfig.js(date).format(formatter({ format: 'long' }));
});
const destination = computed(
    () => (suggestion.value?.packageSet as TripPackageSet).destinationName
);
const origin = computed(
    () => (suggestion.value?.packageSet as TripPackageSet).originName
);
const click = () => {
    if (!suggestion.value) {
        return;
    }
    const path = suggestion.value.packageSet.url;
    const [absolutePath, queryParams] = (path || '').split('#')[0].split('?');
    const query = queryParams
        ? (parse(queryParams, {
              allowDots: true,
          }) as Record<string, any>)
        : undefined;
    if (absolutePath) {
        router.replace({
            path: absolutePath,
            query,
        });
        searchFormStore.shareItineraryInfo(suggestion.value.type);
        searchFormStore.setReserveType(suggestion.value.type);
    }
};
</script>
<template>
    <div v-if="show" class="alternative-suggestion-root">
        <div class="suggestion-section">
            <div class="icon-wrapper">
                <mr-icon
                    v-if="suggestion?.icon"
                    size="large"
                    :icon="suggestion?.icon"
                    color="white"
                />
            </div>
            <div class="content">
                <mr-typography
                    size="large"
                    weight="700"
                    color="var(--blue-700)"
                >
                    سرویس پیشنهادی {{ suggestion?.name }}
                </mr-typography>
                <mr-typography weight="500" color="var(--blue-700)">
                    {{ origin }} به {{ destination }} - {{ departureDate }}
                </mr-typography>
            </div>
        </div>
        <div class="price-section">
            <mr-price-re
                prefix="قیمت‌ها از"
                :price="suggestion?.packageSet.minPrice ?? 0"
                type="small"
            />
            <mr-button
                variant="transparent"
                :left-icon="faAngleLeft"
                size="small"
                @click.stop="click"
            >
                مشاهده و خرید
            </mr-button>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.alternative-suggestion-root {
    background-color: white;
    width: 441px;
    @include shadow();
    padding: 12px;
    display: flex;
    align-items: center;
    gap: 12px;
    border-radius: 12px;
    @include mobile-only {
        max-width: 344px;
        width: 100%;
        flex-direction: column;
        align-items: flex-start;
    }
    .suggestion-section {
        display: flex;
        align-items: center;
        gap: 9px;
        .icon-wrapper {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 48px;
            height: 48px;
            background-color: usePalette();
            border-radius: 10px;
        }
        .content {
            display: flex;
            flex-direction: column;
        }
    }
    .price-section {
        flex-grow: 1;
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        @include mobile-only {
            flex-grow: initial;
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
            width: 100%;
        }
    }
}
</style>
